/*
 OK FBI — Visual Editor postMessage bridge (Slice V1 · read-only).

 Attached to public pages when the URL contains `edit=1`. Emits three
 message kinds to the parent frame:

   { type: "vb:hover",  blockId, blockType, rect: {x,y,w,h} }
   { type: "vb:leave",  blockId }
   { type: "vb:select", blockId, blockType, rect: {x,y,w,h} }

 When `edit=1` is ABSENT (i.e. a normal visitor loads the public site),
 the hook is a strict no-op. No listeners, no observers, no DOM mutation
 — the public visitor experience is byte-identical to pre-V1.

 The bridge NEVER writes to state, never mutates the DOM of the block,
 and never publishes anything. Slice V1 is read-only by contract.
*/
import { useEffect } from "react";

const ORIGIN = "*"; // same-origin frame — parent enforces its own origin check

function isEditMode() {
  try {
    const q = new URLSearchParams(window.location.search);
    return q.get("edit") === "1";
  } catch {
    return false;
  }
}

/** Post a JSON-serialisable payload to the parent window (safe no-op if
 *  the frame is top-level). */
function postToParent(payload) {
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(payload, ORIGIN);
    }
  } catch {
    // ignore — parent may block cross-origin postMessage in rare setups.
  }
}

function findBlock(el) {
  let cur = el;
  while (cur && cur !== document.body) {
    if (cur.dataset && cur.dataset.blockId) return cur;
    cur = cur.parentElement;
  }
  return null;
}

function rectOf(el) {
  const r = el.getBoundingClientRect();
  return {
    x: Math.round(r.left + window.scrollX),
    y: Math.round(r.top  + window.scrollY),
    w: Math.round(r.width),
    h: Math.round(r.height),
  };
}

/** Mount the bridge ONCE per page load. Safe to call from any component
 *  that renders on every public route (e.g. `PublicLayout`). */
export function useEditorBridge() {
  useEffect(() => {
    if (!isEditMode()) return; // strict no-op for real visitors
    let currentHoverId = null;

    const emitLayout = () => {
      const els = Array.from(document.querySelectorAll("[data-block-id]"));
      // Only report blocks that are actually rendered somewhere on the page
      // (visible or hidden) — the parent decides whether to draw "+" tokens
      // between them.
      const list = els.map((el, i) => ({
        blockId: el.dataset.blockId,
        blockType: el.dataset.blockType || "",
        index: i,
        rect: rectOf(el),
      }));
      postToParent({ type: "vb:layout", blocks: list });
    };

    const onMove = (e) => {
      const el = findBlock(e.target);
      if (!el) {
        if (currentHoverId) {
          postToParent({ type: "vb:leave", blockId: currentHoverId });
          currentHoverId = null;
        }
        return;
      }
      const id = el.dataset.blockId;
      if (id === currentHoverId) return;
      currentHoverId = id;
      postToParent({
        type: "vb:hover",
        blockId: id,
        blockType: el.dataset.blockType || "",
        rect: rectOf(el),
      });
    };

    const onClick = (e) => {
      const el = findBlock(e.target);
      if (!el) return;
      // Suppress the click so links inside a block don't navigate the iframe
      // — Slice V1 is a read-only canvas, not an interactive preview.
      e.preventDefault();
      e.stopPropagation();
      postToParent({
        type: "vb:select",
        blockId: el.dataset.blockId,
        blockType: el.dataset.blockType || "",
        rect: rectOf(el),
      });
    };

    const onScrollOrResize = () => {
      // On layout change, if a block is currently hovered, re-emit its
      // rect so the parent overlay stays glued to the block.
      if (currentHoverId) {
        const el = document.querySelector(`[data-block-id="${currentHoverId}"]`);
        if (el) {
          postToParent({
            type: "vb:hover",
            blockId: currentHoverId,
            blockType: el.dataset.blockType || "",
            rect: rectOf(el),
          });
        }
      }
      // Also re-emit the full layout so V3 "+ إضافة" tokens stay glued
      // to the section boundaries.
      emitLayout();
    };

    // Announce readiness + initial layout so the parent can hide the
    // "loading…" placeholder and immediately paint "+ إضافة" tokens.
    postToParent({ type: "vb:ready" });
    // Give React a beat to finish committing before measuring.
    window.setTimeout(emitLayout, 60);
    window.setTimeout(emitLayout, 400);
    window.setTimeout(emitLayout, 1200);

    document.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("click", onClick, true /* capture */);
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);

    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, []);
}

export default useEditorBridge;
